import { Box } from '@factorialco/deprecated-design-system'
import {
  ContactFragment,
  ProjectManagementFinancialDetailsFormQuery,
  ProjectManagementProjectDetailsPageSettingsPageQuery,
} from 'generated/resources/graphql'
import { orderBy } from 'lodash'
import moment from 'moment'

import DayPicker from 'modules/core/components/DayPicker'
import FormRow from 'modules/core/components/FormRow'
import InformationTooltip from 'modules/core/components/Inputs/InformationTooltip'
import Select from 'modules/core/components/Select'
import TextArea from 'modules/core/components/TextArea'
import TextField from 'modules/core/components/TextField'
import { FormErrors } from 'modules/core/hooks/useForm'
import i18n from 'modules/core/lib/i18n'

import { FinanceContactsSelector } from 'modules/finance/components/Contacts/FinanceContactsSelector'
import { financialDetailsAttributes } from 'modules/project_management/lib/constants'
import { projectAttributes } from 'modules/project_management/lib/constants'

export interface ProjectBasicDetailsFormProps {
  onChangeValue: (fieldName: string, fieldValue: string | ContactFragment | undefined) => void
  values: ProjectBasicDetailsValues
  errors: FormErrors<ProjectBasicDetailsValues>
  companyId: number
  hasClientsFeature: boolean
  clientReadOnly?: boolean
  data?:
    | ProjectManagementFinancialDetailsFormQuery
    | ProjectManagementProjectDetailsPageSettingsPageQuery
  hasSubprojectsFeature: boolean
}

export interface ProjectBasicDetailsValues {
  name: string
  code?: string
  description?: string
  startDate?: string
  dueDate?: string
  client: ContactFragment | null
  legalEntityId: string
}

export const ProjectBasicDetailsForm = ({
  values,
  errors,
  onChangeValue,
  companyId,
  hasClientsFeature,
  clientReadOnly = false,
  data,
  hasSubprojectsFeature,
}: ProjectBasicDetailsFormProps) => {
  const { name, code, description, startDate, dueDate, client } = values

  const currencies = data?.apiCore.currencies
  const legalEntities = data?.apiCore.company?.legalEntities

  const createCurrencyOptions = () => {
    if (!legalEntities || !currencies) return null

    const options = orderBy(legalEntities, 'main', 'desc').map((legalEntity) => {
      if (legalEntity?.currency) null
      const currency = currencies.find((value) => value.isoCode === legalEntity?.currency)
      return {
        label: `${legalEntity?.legalName} - ${currency?.symbol} ${currency?.name}`,
        value: `${legalEntity?.id}`,
      }
    })
    return options.filter((option) => option !== null)
  }

  const currencyOptions = createCurrencyOptions()

  return (
    <Box gap="s16">
      <TextField
        label={i18n.t('project_management.modals.create_project.input_labels.project_name')}
        onChange={(value) => onChangeValue(projectAttributes.projectName, value)}
        value={name}
        error={errors.name}
        mandatory
      />
      <TextField
        label={i18n.t('project_management.modals.create_project.input_labels.project_code')}
        onChange={(value) => onChangeValue(projectAttributes.projectCode, value)}
        value={code}
        error={errors.code}
      />
      {hasClientsFeature && (
        <FinanceContactsSelector
          value={client}
          onSelect={(value) => onChangeValue(projectAttributes.client, value ?? undefined)}
          label={i18n.t('project_management.modals.create_project.input_labels.client.label')}
          type="finance_contact"
          queryParams={{
            companyId: companyId,
            contactCreateType: 'client',
          }}
          disabled={clientReadOnly}
          variant={'gamma'}
        />
      )}
      <FormRow multi>
        <DayPicker
          withNull
          label={i18n.t('project_management.modals.create_project.input_labels.start_date')}
          value={startDate}
          onChange={(value) => onChangeValue(projectAttributes.startDate, value)}
          maxDate={dueDate ? moment(dueDate, 'L') : undefined}
          withNullLabel={
            startDate
              ? i18n.t('project_management.settings.sections.basic_information.clear')
              : i18n.t('project_management.settings.sections.basic_information.cancel')
          }
        />
        <DayPicker
          withNull
          label={i18n.t('project_management.modals.create_project.input_labels.due_date')}
          value={dueDate}
          onChange={(value) => onChangeValue(projectAttributes.dueDate, value)}
          minDate={startDate ? moment(startDate, 'L') : undefined}
          withNullLabel={
            dueDate
              ? i18n.t('project_management.settings.sections.basic_information.clear')
              : i18n.t('project_management.settings.sections.basic_information.cancel')
          }
        />
      </FormRow>
      <TextArea
        label={i18n.t('project_management.modals.create_project.input_labels.project_description')}
        onChange={(value) => onChangeValue(projectAttributes.projectDescription, value)}
        value={description}
        error={errors.description}
      />
      {currencyOptions && hasSubprojectsFeature && (
        <Select
          label={i18n.t('project_management.forms.financial_details.legal_entity.label')}
          secondaryLabel={
            <InformationTooltip
              information={i18n.t(
                'project_management.forms.financial_details.legal_entity.information'
              )}
            />
          }
          options={currencyOptions.map((option) => ({
            label: option.label,
            value: option.value,
          }))}
          defaultValue={values.legalEntityId}
          onChange={(value) => onChangeValue(financialDetailsAttributes.legalEntityId, value)}
        />
      )}
    </Box>
  )
}
